import { env } from "cloudflare:workers";
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { eq, isNotNull } from "drizzle-orm";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { getDb } from "@/db";
import { cars, events } from "@/db/schema";

interface AvatarGalleryItem {
	car: {
		id: number;
		carName: string | null;
		driverName: string;
		carNumber: number;
		avatarUrl: string;
		eventId: number;
	};
	eventName: string;
	metadata: Record<string, string>;
}

const getAvatarGallery = createServerFn({ method: "GET" }).handler(async () => {
	const db = getDb();
	const rows = await db
		.select({
			carId: cars.id,
			carName: cars.carName,
			driverName: cars.driverName,
			carNumber: cars.carNumber,
			avatarUrl: cars.avatarUrl,
			eventId: cars.eventId,
			eventName: events.name,
		})
		.from(cars)
		.innerJoin(events, eq(cars.eventId, events.id))
		.where(isNotNull(cars.avatarUrl));

	const items: AvatarGalleryItem[] = [];
	for (const row of rows) {
		let metadata: Record<string, string> = {};
		if (row.avatarUrl) {
			const key = row.avatarUrl.replace(/^\//, "");
			const head = await env.R2.head(key);
			if (head?.customMetadata) {
				metadata = head.customMetadata;
			}
		}
		items.push({
			car: {
				id: row.carId,
				carName: row.carName,
				driverName: row.driverName,
				carNumber: row.carNumber,
				avatarUrl: row.avatarUrl!,
				eventId: row.eventId,
			},
			eventName: row.eventName,
			metadata,
		});
	}
	return items;
});

export const Route = createFileRoute("/admin/images")({
	loader: () => getAvatarGallery(),
	component: AdminImagesPage,
});

function AdminImagesPage() {
	const items = Route.useLoaderData();

	return (
		<div className="min-h-screen bg-background p-6">
			<div className="max-w-6xl mx-auto">
				<Link
					to="/admin/events"
					className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
				>
					<ArrowLeft size={16} />
					Admin
				</Link>

				<h1 className="text-4xl text-foreground mb-8">Image Gallery</h1>

				{items.length === 0 ? (
					<div className="bg-card rounded-xl p-12 text-center">
						<p className="text-foreground text-lg">No avatars generated yet</p>
					</div>
				) : (
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{items.map((item) => (
							<div
								key={item.car.id}
								className="bg-card/50 rounded-xl overflow-hidden border border-border"
							>
								<img
									src={item.car.avatarUrl}
									alt={`${item.car.carName ?? "Car"} avatar`}
									className="w-full aspect-square object-cover"
								/>
								<div className="p-4 space-y-3">
									<div>
										<p className="font-bold text-foreground">
											{item.car.carNumber && (
												<span className="inline-flex items-center text-xs font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5 mr-1.5">
													#{item.car.carNumber}
												</span>
											)}
											{item.car.carName}
										</p>
										<p className="text-muted-foreground text-sm">
											{item.car.driverName}
										</p>
										<p className="text-muted-foreground text-xs">
											{item.eventName}
										</p>
									</div>

									{Object.keys(item.metadata).length > 0 && (
										<div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
											{item.metadata.model && (
												<div>
													<span className="text-muted-foreground">Model:</span>{" "}
													<span className="text-foreground">
														{item.metadata.model}
													</span>
												</div>
											)}
											{item.metadata.mode && (
												<div>
													<span className="text-muted-foreground">Mode:</span>{" "}
													<span className="text-foreground">
														{item.metadata.mode}
													</span>
												</div>
											)}
											{item.metadata.quality && (
												<div>
													<span className="text-muted-foreground">
														Quality:
													</span>{" "}
													<span className="text-foreground">
														{item.metadata.quality}
													</span>
												</div>
											)}
											{item.metadata.duration_ms && (
												<div>
													<span className="text-muted-foreground">
														Duration:
													</span>{" "}
													<span className="text-foreground">
														{(Number(item.metadata.duration_ms) / 1000).toFixed(
															1,
														)}
														s
													</span>
												</div>
											)}
											{item.metadata.input_tokens && (
												<div>
													<span className="text-muted-foreground">
														In tokens:
													</span>{" "}
													<span className="text-foreground">
														{Number(
															item.metadata.input_tokens,
														).toLocaleString()}
													</span>
												</div>
											)}
											{item.metadata.output_tokens && (
												<div>
													<span className="text-muted-foreground">
														Out tokens:
													</span>{" "}
													<span className="text-foreground">
														{Number(
															item.metadata.output_tokens,
														).toLocaleString()}
													</span>
												</div>
											)}
											{item.metadata.generated_at && (
												<div className="col-span-2">
													<span className="text-muted-foreground">
														Generated:
													</span>{" "}
													<span className="text-foreground">
														{new Date(
															item.metadata.generated_at,
														).toLocaleString()}
													</span>
												</div>
											)}
										</div>
									)}

									<Link
										to="/admin/events/$eventId/cars"
										params={{ eventId: String(item.car.eventId) }}
										className="inline-flex items-center gap-1 text-xs text-primary hover:text-accent transition-colors"
									>
										<ExternalLink size={12} />
										Manage car
									</Link>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
